<script setup lang="ts">
import CustomModal from "@/components/CustomModal/CustomModal.vue";
import CustomButton from "@/components/Button/Button.vue";
import {useI18n} from 'vue-i18n';
import {computed, onMounted, ref, watch} from "vue";
import {CrewMember} from "@/models/CrewMember";
import {getAllCountries} from "@/services/country.service";
import {useCrewStore} from "@/stores/crewStore";
import {useDocumentUpload} from "@/composables/useDocumentUpload";
import {formatSize} from "@/composables/useFileUpload";
import {vMaska} from "maska/vue";
import FileUpload from "@/components/FileUpload/FileUpload.vue";
import {useValidationRules} from "@/composables/useValidationRules";
import {useRoute} from "vue-router";
import * as DateUtils from "@/helpers/dateUtils";
import CustomSpinner from "@/components/CustomSpinner/CustomSpinner.vue";
import {VueSpinner} from "vue3-spinners";

const i18n = useI18n({ useScope: "global" });

const props = defineProps<{
  showEditModal: boolean;
  member: CrewMember;
}>();
const emit = defineEmits(["on-close"]);

const { t,locale } = useI18n();


const { passportExpiryDateRule, passportNumberRule } = useValidationRules();

// Make passport validations optional: only validate if user typed something
const optionalPassportNumberRule = (v: string) => {
  if (v == null || String(v).trim() === '') return true;
  return passportNumberRule(v);
};
const optionalpassportExpiryDateRule = (v: string) => {
  if (v == null || String(v).trim() === '') return true;
  return passportExpiryDateRule(v);
};

const countries = ref([]);
const crewStore = useCrewStore();
const route = useRoute();
// Prefer route param, fall back to store if available
const currentListUuid = computed(() => (route.params.id as string) || crewStore.listUuid || '');

const isLoading = ref(true);
const isDragging = ref(false);
const toggleValue = ref(false);
const vhfToggleValue = ref(false);
const licenseInputRef = ref(null);
const vhflicenseInputRef = ref(null);
const passportInputRef = ref(null);

const showAddNewCrewList = ref(false);

const formRef = ref();
const isSaving = ref(false);
const submitError = ref<string | null>(null);
const isFormValid = ref(false);


const dateInputConfig = computed(() =>
    DateUtils.getDateMaskAndPlaceholder(locale.value)
);


async function validateAndSubmit() {
  if (isSaving.value) return;
  submitError.value = null;

  let isValid = true;
  try {
    const validateFn = formRef.value?.validate;
    if (typeof validateFn === 'function') {
      const result = await validateFn();
      isValid = typeof result === 'boolean' ? result : !!result?.valid;
    }
  } catch (e) {
    isValid = true;
  }
  console.log("IS VALID? ", isValid)
  if (!isValid) return;

  try {
    isSaving.value = true;
    await onSubmit();
  } finally {
    isSaving.value = false;
  }
}

const {
  uploadedLicense,
  uploadedPassport,
  uploadedVHFLicense,
  licenseUploadError,
  passportUploadError,
  vhfLicenseUploadError,
  handleFileInput,
  handleFileDrop,
  removeUploadedFile,
} = useDocumentUpload({
  uploadPassportDocument: (file: File) =>
    crewStore.uploadPassportDocument(currentListUuid.value, props.member.uuid, file),
  uploadLicenseDocument: (file: File) =>
    crewStore.uploadLicenseDocument(currentListUuid.value, props.member.uuid, file),
  uploadVHFLicenseDocument: (file: File) =>
    crewStore.uploadVHFLicenseDocument(currentListUuid.value, props.member.uuid, file),
  deletePassportDocument: (uuid: string) =>
    crewStore.deletePassportDocument(currentListUuid.value, props.member.uuid, uuid),
  deleteLicenseDocument: (uuid: string) =>
    crewStore.deleteLicenseDocument(currentListUuid.value, props.member.uuid, uuid),
  deleteVHFLicenseDocument: (uuid: string) =>
    crewStore.deleteVHFLicenseDocument(currentListUuid.value, props.member.uuid, uuid),
});


function triggerLicenseUpload() {
  licenseInputRef.value?.click();
}

function triggerVhfLicenseUpload() {
  vhflicenseInputRef.value?.click();
}

function triggerPassportUpload() {
  passportInputRef.value?.click();
}


const formData = ref({
  firstName: props.member.firstName,
  lastName: props.member.lastName,
  nationality: props.member.nationality,
  birthDate: props.member.birthDate ? DateUtils.fromApiDate(props.member.birthDate, i18n.locale.value) : null,
  passportNumber: props.member.passportNumber,
  passportExpiryDate: props.member.passportExpiryDate ? DateUtils.fromApiDate(props.member.passportExpiryDate, i18n.locale.value) : null ,
  email: props.member.email,
  phoneNumber: props.member.phone
})

onMounted(async () => {
  try {
    isLoading.value = true;

    // Load countries for the nationality dropdown
    const response = await getAllCountries();
    countries.value = response.data._embedded?.countries || [];

    if (props.member.passportDocuments?.length) {
      uploadedPassport.value = props.member.passportDocuments.map((doc: any) => ({
        uuid: doc.uuid,
        name: doc.fileName,
        size: formatSize(doc.size || 0),
        status: 'uploaded',
      }));
    }
    if (props.member.licenseDocuments?.length) {
      uploadedLicense.value = props.member.licenseDocuments.map((doc: any) => ({
        uuid: doc.uuid,
        name: doc.fileName,
        size: formatSize(doc.size || 0),
        status: 'uploaded',
      }));
      toggleValue.value = true;
    }
    if (props.member.vhfLicenseDocuments?.length) {
      uploadedVHFLicense.value = props.member.vhfLicenseDocuments.map((doc: any) => ({
        uuid: doc.uuid,
        name: doc.fileName,
        size: formatSize(doc.size || 0),
        status: 'uploaded',
      }));
      vhfToggleValue.value = true;
    }
  } catch (e) {
    console.error('Failed to load countries:', e);
    countries.value = [];
  } finally {
    isLoading.value = false;
  }
});

async function onSubmit() {
  if (!currentListUuid.value) {
    console.error('Missing crew list UUID for update');
    return;
  }

  const payload: any = {
    firstName: formData.value.firstName,
    lastName: formData.value.lastName,
    nationality: {"id":formData.value.nationality},
    birthDate: formData.value.birthDate,
    passportNumber: formData.value.passportNumber,
    passportExpiryDate: formData.value.passportExpiryDate,
    email: formData.value.email,
    phoneNumber: formData.value.phoneNumber,
  };

  console.log("Final payload sent to updateCrewMember:", payload);

  try {
    await crewStore.updateCrewMember(
      currentListUuid.value,
      props.member.uuid,
      payload
    );
    console.log("SENT")
    await crewStore.fetchCrewList(currentListUuid.value);

    showAddNewCrewList.value = false;
    document.documentElement.style.overflow = "auto";
    emit("on-close");
  } catch (err) {
    console.error("Failed to update crew member:", err);
  }
}
// Body scroll lock if popup displayed
watch(
  () => props.showEditModal,
  (newValue) => {
  if (newValue) {
    document.documentElement.style.overflow = 'hidden';
  } else {
    document.documentElement.style.overflow = 'auto';
  }
});
</script>

<template>
  <CustomModal :show="showEditModal" :custom-index="40" :mobile-fullscreen="true" @on-close="$emit('on-close')"  class="test" extra-classes="h-fit" drawerHeight="fit-content">
    <div class="flex flex-col py-10 sm:p-0 md:p-5 container min-h-0 sm:h-auto md:max-h-[85vh]">
      <div class="cursor-pointer self-end sticky top-0 z-10 bg-white pb-4" @click="$emit('on-close')">
        <img src="@/assets/icons/svg/Close_16x16.svg" alt="Close" width="18" />
      </div>
      <div class="overflow-y-auto max-h-screen">
        <div class="sm:overflow-y-auto sm:p-0 md:pr-5 mb-25">
          <div class="px-2 sm:px-5 pt-4">
            <div class="responsive-title-4 font-semibold">{{ t('modal.editCrewMember') }}</div>
            <div class="responsive-body-small font-semibold bg-notifications-pending py-2.5 px-5 mt-11 rounded-8xl">
              {{ t('modal.warning') }}
            </div>

            <VForm ref="formRef" v-model="isFormValid" validate-on="input" @submit.prevent="validateAndSubmit">
              <div class="mt-8 grid gap-y-6">
                <div class="grid grid-cols-2 gap-x-8 gap-y-6">
                  <VTextField v-model="formData.firstName" :label="`${t('modal.firstName')} *`" variant="underlined" :rules="[v => !!(v && String(v).trim()) || t('profile.firstNameRequired')]" />
                  <VTextField v-model="formData.lastName" :label="`${t('modal.lastName')} *`" variant="underlined" :rules="[v => !!(v && String(v).trim()) || t('profile.lastNameRequired')]" />
                </div>
                <div class="grid grid-cols-2 gap-x-8 gap-y-6">
                  <VSelect
                    v-model="formData.nationality"
                    :label="`${t('modal.nationality')} *`"
                    variant="underlined"
                    item-title="name"
                    item-value="id"
                    :items="countries"
                    :return-object="false"
                    :rules="[v => !!(v && String(v).trim()) || t('profile.nationalityRequired')]"
                  />
                  <VTextField
                    v-model="formData.birthDate"
                    :label="`${t('modal.dob')} *`"
                    type="text"
                    variant="underlined"
                    v-maska="dateInputConfig.mask"
                    :placeholder="dateInputConfig.placeholder"
                    :rules="[v => !!(v && String(v).trim()) || t('profile.birthdateRequired')]"
                  />
                  <VTextField
                    v-model="formData.email"
                    :label="`${t('modal.email')} *`"
                    variant="underlined"
                    type="email"
                    :rules="[
                      v => !!(v && String(v).trim()) || t('profile.emailRequired'),
                      v => /.+@.+\..+/.test(v) || t('profile.emailInvalid')
                    ]"
                  />
                  <VTextField
                    v-model="formData.phoneNumber"
                    :label="`${t('modal.phoneNumber')} *`"
                    variant="underlined"
                    :rules="[v => !!(v && String(v).trim()) || t('profile.phoneRequired')]"
                  />
                </div>
                <div>
                  <span class="responsive-body-medium">{{ t('profile.passportPhoto') }}</span>
                  <FileUpload
                      label="profile.uploadLicenseFiles"
                      type="passport"
                      :uploadFiles="uploadedPassport"
                      :uploadError="passportUploadError"
                      :isDragging="isDragging"
                      :onUploadTrigger="triggerPassportUpload"
                      :onDrop="handleFileDrop"
                      :onFileChange="handleFileInput"
                      :onRemove="removeUploadedFile"
                      :showFileName="true"
                  />
                </div>
                <div class="grid grid-cols-2 gap-x-8 gap-y-6">
                <VTextField v-model="formData.passportNumber"
                            :label="t('modal.passportNumber')"
                            variant="underlined"
                            type="pass_number"
                            :rules="[optionalPassportNumberRule]"
                />
                  <VTextField
                      v-model="formData.passportExpiryDate"
                      :label="t('modal.passportExpiryDate')"
                      :rules="[optionalpassportExpiryDateRule]"
                      hint=" "
                      persistent-hint
                      variant="underlined"
                      type="text"
                      v-maska="dateInputConfig.mask"
                      :placeholder="dateInputConfig.placeholder"
                  />
                </div>
                <div>
                  <div class="responsive-body-medium align-center flex gap-6 ml-5">
                    <VSwitch
                        v-model="toggleValue"
                        inset
                        base-color="grey"
                        color="black"
                        hide-details
                        density="compact"
                    />
                    {{ t('profile.sailingLicense') }}
                  </div>
                  <FileUpload
                      v-if="toggleValue"
                      label="profile.uploadLicenseFiles"
                      type="license"
                      :uploadFiles="uploadedLicense"
                      :uploadError="licenseUploadError"
                      :onUploadTrigger="triggerLicenseUpload"
                      :onDrop="handleFileDrop"
                      :onFileChange="handleFileInput"
                      :onRemove="removeUploadedFile"
                      :showFileName="true"
                  />
                </div>
                <div>
                  <div class="responsive-body-medium align-center flex gap-6 ml-5">
                    <VSwitch
                        v-model="vhfToggleValue"
                        inset
                        base-color="grey"
                        color="black"
                        hide-details
                        density="compact"
                    />
                    {{ t('profile.vhfLicense') }}
                  </div>
                  <FileUpload
                      v-if="vhfToggleValue"
                      label="profile.uploadLicenseFiles"
                      type="vhf"
                      :uploadFiles="uploadedVHFLicense"
                      :uploadError="vhfLicenseUploadError"
                      :isDragging="isDragging"
                      :onUploadTrigger="triggerVhfLicenseUpload"
                      :onDrop="handleFileDrop"
                      :onFileChange="handleFileInput"
                      :onRemove="removeUploadedFile"
                      :showFileName="true"
                  />
                </div>
                <div class="flex justify-between align-center pb-4">
                  <CustomButton
                      type="black"
                      class="self-start responsive-body-medium sm:w-25 h-13"
                      :rounded="true"
                      :outlined="false"
                      :disabled="isSaving"
                      :aria-busy="isSaving"
                      @click="validateAndSubmit">
                    <span class="flex items-center gap-2">
                      <VueSpinner v-if="isSaving" :size="16" color="#FFFFFF"/>
                      <span>{{ t('tripEditModal.updateButton') }}</span>
                    </span>
                  </CustomButton>
                </div>
              </div>
            </VForm>
          </div>
        </div>
      </div>
    </div>

  </CustomModal>
</template>

<style scoped>
:deep(.v-switch__track) {
  opacity: 1;
}


:deep(.modal-content) {
  min-width: 760px;
}


@media (max-width: 800px) {
  :deep(.modal-content) {
    min-width: auto;
    width: 100%;
  }
}

</style>
